/**
 * Incident Brief Share Card — a polished, light-theme, "ready to share"
 * summary of an analyzed incident. Pure presentational; derives every
 * value from the persisted incident object.
 *
 * Intentionally renders as a self-contained square-ish block so it can
 * be exported as a PNG via html-to-image and pasted into chats / decks.
 */
import { forwardRef } from "react";
import {
  Flag,
  Users,
  Search,
  ShieldCheck,
  Rocket,
  ShieldAlert,
  Link2,
  Copy,
  CheckCircle2,
  BarChart3,
} from "lucide-react";

// -----------------------------
// RAG → palette
// -----------------------------
const RAG_STYLES = {
  green: {
    ring: "#10b981",
    pillBg: "bg-emerald-50",
    pillBorder: "border-emerald-200",
    pillText: "text-emerald-700",
    dot: "bg-emerald-500",
    softBg: "bg-emerald-50/60",
    tileBg: "bg-emerald-50",
    tileBorder: "border-emerald-200",
    icon: "text-emerald-600",
  },
  amber: {
    ring: "#f59e0b",
    pillBg: "bg-amber-50",
    pillBorder: "border-amber-200",
    pillText: "text-amber-700",
    dot: "bg-amber-500",
    softBg: "bg-amber-50/60",
    tileBg: "bg-amber-50",
    tileBorder: "border-amber-200",
    icon: "text-amber-600",
  },
  red: {
    ring: "#ef4444",
    pillBg: "bg-rose-50",
    pillBorder: "border-rose-200",
    pillText: "text-rose-700",
    dot: "bg-rose-500",
    softBg: "bg-rose-50/60",
    tileBg: "bg-rose-50",
    tileBorder: "border-rose-200",
    icon: "text-rose-600",
  },
};

// Small inline score ring (svg). Designed to render correctly inside
// html-to-image's PNG capture path.
function ScoreRing({ score, rag, size = 76 }) {
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, Number(score || 0) / 10));
  const dash = c * pct;
  const styles = RAG_STYLES[rag] || RAG_STYLES.amber;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="white" stroke="#e5e7eb" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={styles.ring}
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${c - dash}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex items-baseline justify-center gap-0.5 pt-[6px]">
        <span className="text-[18px] font-heading font-black tracking-tight text-slate-900">
          {Number(score || 0).toFixed(1)}
        </span>
        <span className="text-[9px] font-mono text-slate-500 ml-0.5">/10</span>
      </div>
    </div>
  );
}

function ConfidenceDots({ filled, rag }) {
  const dotOn = (RAG_STYLES[rag] || RAG_STYLES.amber).dot;
  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={`h-2 w-2 rounded-full ${i < filled ? dotOn : "bg-slate-200"}`}
        />
      ))}
    </div>
  );
}

// Tiny bar chart for the trailing CRE Score block (matches the reference).
function MiniBars({ rag }) {
  const fill = (RAG_STYLES[rag] || RAG_STYLES.amber).ring;
  return (
    <svg width="60" height="42" viewBox="0 0 60 42" aria-hidden>
      <rect x="2"  y="28" width="10" height="12" rx="2" fill={fill} opacity="0.45" />
      <rect x="16" y="18" width="10" height="22" rx="2" fill={fill} opacity="0.65" />
      <rect x="30" y="10" width="10" height="30" rx="2" fill={fill} opacity="0.85" />
      <rect x="44" y="2"  width="10" height="38" rx="2" fill={fill} />
    </svg>
  );
}

// -----------------------------
// Row block — icon tile + label + value (+ optional trailing slot)
// -----------------------------
function Row({ icon: Icon, iconBg, iconColor, label, value, trailing, last }) {
  return (
    <div className={`flex items-start gap-3 py-3 ${last ? "" : "border-b border-slate-100"}`}>
      <div className={`h-11 w-11 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
        <Icon className={`h-5 w-5 ${iconColor}`} strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold text-slate-500 uppercase tracking-wide">
          {label}
        </div>
        <div className="mt-0.5 text-[15px] sm:text-base font-semibold text-slate-900 leading-snug break-words">
          {value || "—"}
        </div>
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </div>
  );
}

// -----------------------------
// The main share card. forwardRef so the parent dialog can pass a ref
// to html-to-image for PNG export.
// -----------------------------
const IncidentBriefShareCard = forwardRef(function IncidentBriefShareCard(
  { brief },
  ref,
) {
  if (!brief) return null;
  const rag = brief.score_rag || "amber";
  const ragStyles = RAG_STYLES[rag];
  const confStyles = RAG_STYLES[brief.confidence_rag || "amber"];
  const approvalStyles = brief.approval_state === "completed"
    ? RAG_STYLES.green
    : RAG_STYLES.red;

  return (
    <div
      ref={ref}
      data-testid="incident-brief-share-card"
      className="bg-white rounded-3xl border border-slate-200 shadow-[0_18px_48px_-24px_rgba(15,23,42,0.25)] px-5 sm:px-7 py-6 sm:py-7 w-full max-w-[460px] mx-auto"
    >
      {/* Header — branding + Ready to Share pill */}
      <div className="flex items-start justify-between gap-3">
        <div className="font-heading font-black tracking-tight text-slate-900 text-[26px] leading-none">
          <span>CREAI</span>
          <span className="text-sky-600">Ops</span>
        </div>
        <span
          data-testid="ready-to-share-pill"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11.5px] font-semibold"
        >
          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.25} />
          Ready to Share
        </span>
      </div>

      <h2 className="mt-3 font-heading font-black tracking-tight text-slate-900 text-[28px] sm:text-[32px] leading-[1.05]">
        Incident Brief
      </h2>
      <div className="mt-3 h-px bg-slate-100" />

      {/* Rows */}
      <div className="mt-1">
        <Row
          icon={Flag}
          iconBg="bg-sky-50"
          iconColor="text-sky-600"
          label="Incident"
          value={brief.title}
        />
        <Row
          icon={Users}
          iconBg="bg-teal-50"
          iconColor="text-teal-600"
          label="Impact"
          value={brief.impact}
        />
        <Row
          icon={Search}
          iconBg="bg-indigo-50"
          iconColor="text-indigo-600"
          label="Likely RCA"
          value={brief.likely_rca}
        />
      </div>

      {/* CRE Score block (prominent) */}
      <div
        data-testid="brief-cre-score-block"
        className={`mt-3 rounded-2xl ${ragStyles.tileBg} border ${ragStyles.tileBorder} px-4 py-3.5 flex items-center gap-3`}
      >
        {brief.score_value != null ? (
          <ScoreRing score={brief.score_value} rag={rag} />
        ) : (
          <div className="h-[76px] w-[76px] rounded-full border-[6px] border-slate-200 flex items-center justify-center bg-white shrink-0">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
              Pending
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 text-[15px]">CRE Score:</div>
          <div className="text-[12.5px] text-slate-600 leading-snug">
            Reasoning quality and readiness
          </div>
          {brief.score_label ? (
            <span
              data-testid="brief-score-label-pill"
              className={`mt-1.5 inline-flex items-center px-2.5 py-0.5 rounded-full border text-[11.5px] font-semibold ${ragStyles.pillBg} ${ragStyles.pillBorder} ${ragStyles.pillText}`}
            >
              {brief.score_label}
            </span>
          ) : brief.score_value == null ? (
            <span
              data-testid="brief-score-label-pill"
              className="mt-1.5 inline-flex items-center px-2.5 py-0.5 rounded-full border text-[11.5px] font-semibold bg-slate-50 border-slate-200 text-slate-600"
            >
              Re-run to score
            </span>
          ) : null}
        </div>
        {brief.score_value != null ? (
          <div className="hidden sm:block pl-2 border-l border-slate-200/70">
            <MiniBars rag={rag} />
          </div>
        ) : null}
      </div>

      {/* Confidence + Next Action + Approval rows */}
      <div className="mt-1">
        <Row
          icon={ShieldCheck}
          iconBg={confStyles.tileBg}
          iconColor={confStyles.icon}
          label="Confidence"
          value={
            <div className="flex items-center gap-2 flex-wrap">
              <ConfidenceDots
                filled={brief.confidence_dots || 0}
                rag={brief.confidence_rag || "amber"}
              />
            </div>
          }
          trailing={
            <span
              data-testid="brief-confidence-pill"
              className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[11.5px] font-semibold ${confStyles.pillBg} ${confStyles.pillBorder} ${confStyles.pillText}`}
            >
              {brief.confidence_label}
            </span>
          }
        />
        <Row
          icon={Rocket}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
          label="Next Action"
          value={brief.next_action}
        />
        <Row
          icon={ShieldAlert}
          iconBg={approvalStyles.tileBg}
          iconColor={approvalStyles.icon}
          label="Approval"
          value={brief.approval_text}
          last
          trailing={
            <span
              data-testid="brief-approval-pill"
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11.5px] font-semibold ${approvalStyles.pillBg} ${approvalStyles.pillBorder} ${approvalStyles.pillText}`}
            >
              <ShieldAlert className="h-3 w-3" strokeWidth={2.25} />
              {brief.approval_pill}
            </span>
          }
        />
      </div>

      {/* Analysis link strip */}
      <a
        data-testid="brief-analysis-link"
        href={brief.full_link_href}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 flex items-center gap-3 rounded-2xl border border-sky-200 bg-sky-50/60 px-3.5 py-3 hover:bg-sky-50 transition-colors"
      >
        <div className="h-10 w-10 rounded-xl bg-sky-100 border border-sky-200 flex items-center justify-center shrink-0">
          <Link2 className="h-4.5 w-4.5 text-sky-600" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] sm:text-[15px] font-semibold text-sky-700">
            View Full Incident Analysis
          </div>
          <div className="text-[12px] text-slate-500 truncate">
            {brief.link_display}
          </div>
        </div>
        <Copy className="h-4 w-4 text-sky-600 shrink-0" strokeWidth={1.75} />
      </a>
    </div>
  );
});

export default IncidentBriefShareCard;
export { RAG_STYLES };
